import { FC, ReactNode, createContext, useCallback, useContext, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { NewChatWarningDialog } from '../../components/chat/NewChatWarningDialog';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { isAgentSessionActive } from '../models/ChatMessage';
import { CustomerLiveChatService } from '../services/CustomerLiveChatService';

/**
 * Gate a "start a new chat" action behind the #1937 warning: if the selected conversation is a live
 * chat the customer would abandon — a human agent is handling it, or it's waiting in the agent queue
 * — confirm with the customer and close that chat before running `proceed`; otherwise run `proceed`
 * immediately. `proceed` carries each call site's own post-create behavior (createChat + navigation /
 * app-state changes), so the guard stays a thin pre-check that doesn't need to know what "new chat"
 * means for each entry point.
 */
export type RequestNewChat = (proceed: () => void | Promise<void>) => void;

const NewChatGuardContext = createContext<RequestNewChat>((proceed) => {
    void proceed();
});

export const useRequestNewChat = (): RequestNewChat => useContext(NewChatGuardContext);

// Which live-chat state the customer is about to abandon — drives the warning copy ("lose your live
// chat" vs "lose your place in the queue"). Both confirm to the same close call.
type CloseReason = 'human' | 'queued';

interface PendingRequest {
    proceed: () => void | Promise<void>;
    chatId: string;
    reason: CloseReason;
}

/**
 * Provides {@link useRequestNewChat} and renders the single shared warning dialog. Mounted once high
 * in the customer app tree so every new-chat entry point (chat headers, history/settings, the user
 * menu) routes through one guard — no path can silently abandon an active live chat.
 */
export const NewChatGuardProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const auth = useAuth();
    const selectedId = useAppSelector((state: RootState) => state.conversations.selectedId);
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    // Two-step read (mirrors EscalateToHumanButton) so a selectedId with no loaded conversation
    // yields undefined instead of throwing, and the optional chain below stays type-correct.
    const conversation = useAppSelector((state: RootState) =>
        selectedId ? state.conversations.conversations[selectedId] : undefined,
    );
    const messages = conversation?.messages;
    // Master switch: skip the queued-state probe entirely when LiveChat is off, so plain deployments
    // never fire the mode read on a new-chat click.
    const liveChatEnabled = useAppSelector((state: RootState) => state.app.serviceInfo.featureFlags.LiveChatEnabled);
    const token = auth.user?.access_token;

    const [pending, setPending] = useState<PendingRequest | null>(null);
    const [closing, setClosing] = useState(false);

    // Latest selectedId, read after the async queued probe to detect a chat switch mid-probe.
    const selectedIdRef = useRef(selectedId);
    selectedIdRef.current = selectedId;
    // In-flight guard for the async queued probe: without it a fast double-click (the probe awaits a
    // network round trip with no visual feedback) would fire two probes and two proceed() → two chats.
    const probingRef = useRef(false);
    // Same guard for the confirm path: the dialog's disabled state only lands on the next render
    // (after setClosing), so a same-tick double-fire (touch+click, Enter repeat) would otherwise run
    // handleConfirm twice before it commits → two closeAsync + two proceed() → two chats.
    const confirmingRef = useRef(false);

    const requestNewChat = useCallback<RequestNewChat>(
        (proceed) => {
            // Human handover is derivable instantly from the transcript (no round-trip).
            // Known limitation (deferred, PR #2104): this fast-path doesn't cancel a probe still in
            // flight from an earlier click, so a tight interleaving — probe running, an AgentJoined
            // streams in, then a second click lands here — could let the stale probe still fire
            // proceed()/overwrite the dialog. Contrived timing; revisit if it surfaces in production.
            if (selectedId && isAgentSessionActive(messages ?? [])) {
                setPending({ proceed, chatId: selectedId, reason: 'human' });
                return;
            }
            // Queued has no transcript marker, so probe the chat's mode — but only when LiveChat is on
            // and we have a chat + token. Best-effort: any failure (or a non-live mode) just proceeds
            // to the new chat, never trapping the customer on a slow/failed read.
            if (!selectedId || !token || !liveChatEnabled) {
                void proceed();
                return;
            }
            if (probingRef.current) {
                return; // a probe is already deciding this click; ignore the re-entrant one.
            }
            probingRef.current = true;
            const chatIdAtClick = selectedId;
            void (async () => {
                try {
                    const { mode } = await new CustomerLiveChatService(token).getModeAsync(chatIdAtClick, hostContext);
                    // Also warn on Human here, not just Queued: the transcript check above misses an
                    // agent-handled chat whose AgentJoined notice hasn't hydrated into Redux yet (fresh
                    // reload / embed init), and the probe carries the authoritative mode — so without
                    // this a genuinely Human chat would get a new chat with no warning/close, stranding
                    // the agent (the exact #1937 case).
                    if ((mode === 'Queued' || mode === 'Human') && selectedIdRef.current === chatIdAtClick) {
                        probingRef.current = false;
                        setPending({ proceed, chatId: chatIdAtClick, reason: mode === 'Human' ? 'human' : 'queued' });
                        return;
                    }
                } catch {
                    // swallow — fall through to the chat-switch-guarded proceed below.
                }
                probingRef.current = false;
                // Only run the new-chat action if the customer is still on the chat they clicked from.
                // If they switched conversations while the probe was in flight, that original intent is
                // stale — proceeding would create a new chat for a conversation they've moved on from
                // (or, on the error path, act on the wrong chat). A later click re-probes the current one.
                if (selectedIdRef.current === chatIdAtClick) {
                    void proceed();
                }
            })();
        },
        [selectedId, messages, token, liveChatEnabled, hostContext],
    );

    const handleConfirm = useCallback(() => {
        if (!pending || confirmingRef.current) {
            return;
        }
        confirmingRef.current = true;
        const { proceed, chatId } = pending;

        void (async () => {
            setClosing(true);
            try {
                // Best-effort: the server-side resolved-chat lock (#1938) is the real guard, so even
                // if this close fails we still let the customer start their new chat rather than
                // trapping them. A failure just means the agent's active list / queue clears on its
                // next poll instead of instantly. No token (mid-renew) → skip the call for the same
                // reason; a customer in a live chat / queue always has one in practice. The same close
                // call handles both cases: the backend closes a Human chat or removes a Queued one.
                if (token) {
                    await new CustomerLiveChatService(token).closeAsync(chatId, hostContext);
                }
            } catch {
                // swallow — proceed regardless (see above).
            } finally {
                setClosing(false);
                setPending(null);
                confirmingRef.current = false;
                await proceed();
            }
        })();
    }, [pending, token, hostContext]);

    const handleCancel = useCallback(() => {
        setPending(null);
    }, []);

    return (
        <NewChatGuardContext.Provider value={requestNewChat}>
            {children}
            <NewChatWarningDialog
                open={pending !== null}
                reason={pending?.reason ?? 'human'}
                busy={closing}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </NewChatGuardContext.Provider>
    );
};
